import { useCallback, useEffect, useState } from 'react';
import {
    MenuItem,
    Select,
    FormControl,
    Checkbox,
    Box,
    TableRow,
    TableCell,
    Tooltip,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableBody,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { makeFormStyles } from 'forms/styles';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { MdDelete, MdDragHandle } from 'react-icons/md';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { setProcedureCreateNodeData } from 'features/procedures/proceduresSlice';
import NodeTypeButton from './NodeTypeButton';
import { FormTextField } from 'components/TextField';
import { useDragEnd } from '../useDragEnd';

const ButtonGroupField = ({ values, setFieldValue, node, nodes }) => {
    const [onDragEnd, handleRemove] = useDragEnd();

    return (
        <DragDropContext
            onDragEnd={(result) => onDragEnd(result, values.option_buttons, 'option_buttons', setFieldValue)}
        >
            <TableContainer sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{/* drag handle */}</TableCell>
                            <TableCell>Text</TableCell>
                            <TableCell>Target Node</TableCell>
                            <TableCell>Answer Value</TableCell>
                            <TableCell>Style</TableCell>
                            <TableCell padding="none">Incorrect?</TableCell>
                            <TableCell>{/* delete */}</TableCell>
                        </TableRow>
                    </TableHead>
                    <Droppable droppableId="droppable-content-nodes-buttons">
                        {(provided) => (
                            <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                                {values.option_buttons?.map((button, idx) => {
                                    return (
                                        <ButtonField
                                            button={button}
                                            key={`ButtonField${idx}`}
                                            idx={idx}
                                            node={node}
                                            nodes={nodes}
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            provided={provided}
                                            handleRemoveOptionButton={(id) =>
                                                handleRemove(id, values.option_buttons, 'option_buttons', setFieldValue)
                                            }
                                        />
                                    );
                                })}
                                {provided.placeholder}
                            </TableBody>
                        )}
                    </Droppable>
                </Table>
            </TableContainer>
        </DragDropContext>
    );
};

const ButtonField = ({ button, idx, nodes, node, values, setFieldValue, handleRemoveOptionButton, ...otherProps }) => {
    const { errors } = useFormikContext();
    const dispatch = useDispatch();
    const validationError = errors?.[`option_buttons[${idx}].button_text`];
    useEffect(() => {
        if (node?.focusTargetBtnId === 0 || node?.focusTargetBtnId) {
            let ele = document.getElementById(`target-node-id-${node?.focusTargetBtnId}`);
            if (ele) ele.scrollIntoView({ behavior: 'smooth' });
            ele = document.getElementById(`target_node_id_${node?.focusTargetBtnId}`);
            if (ele) ele.focus();
        }
    }, [node]);

    const [openNodeTypeSelectionDialog, setOpenNodeTypeSelectionDialog] = useState(false);
    const theme = useSelector(selectDefaultTheme);
    nodes = nodes.filter((nd) => nd.id !== node.id);
    const commonFormStyles = makeFormStyles(theme);

    const handleButtonSelection = useCallback(
        (nodeType) => {
            dispatch(
                setProcedureCreateNodeData({
                    currentNode: openNodeTypeSelectionDialog,
                    newNodeType: nodeType,
                })
            );
        },
        [openNodeTypeSelectionDialog]
    );

    return (
        <>
            <Draggable
                key={'button_opts_draggable_' + idx}
                draggableId={String('button_opts_draggable_' + idx)}
                index={idx}
            >
                {(provided) => (
                    <TableRow hover key={button.button_id} ref={provided.innerRef} {...provided.draggableProps}>
                        <TableCell padding="none">
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                {...provided.dragHandleProps}
                            >
                                <MdDragHandle className="react-icon" />
                            </Box>
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                            <Tooltip title={validationError}>
                                <FormControl fullWidth minWidth>
                                    <FormTextField
                                        sx={{ m: 0, minWidth: '100px' }}
                                        placeholder="Text"
                                        name={`option_buttons[${idx}].button_text`}
                                        size="small"
                                        onChange={(e) => {
                                            const currOptions = [...values.option_buttons];
                                            currOptions[idx] = {
                                                ...values.option_buttons[idx],
                                                button_text: e.target.value,
                                            };
                                            setFieldValue(`option_buttons`, currOptions);
                                        }}
                                        {...otherProps}
                                        error={!!errors?.[`option_buttons[${idx}].button_text`]}
                                    />
                                </FormControl>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                            <FormControl fullWidth>
                                <Select
                                    className={commonFormStyles.materialSelect}
                                    style={{ margin: 0 }}
                                    labelId={`target-node-id-${idx}`}
                                    id={`target_node_id_${idx}`}
                                    placeholder="Target Node"
                                    name={`option_buttons[${idx}].target_node_id`}
                                    size="small"
                                    value={button?.action?.id}
                                    onChange={(e) => {
                                        const currOptions = [...values.option_buttons];
                                        currOptions[idx] = {
                                            ...values.option_buttons[idx],
                                            action: {
                                                ...values.option_buttons[idx].action,
                                                id: e.target.value,
                                            },
                                        };
                                        if (e.target.value === 'new-node') {
                                            setOpenNodeTypeSelectionDialog({
                                                ...values,
                                                option_buttons: currOptions,
                                                focusTargetBtnId: idx,
                                            });
                                        } else {
                                            setFieldValue('option_buttons', currOptions);
                                        }
                                    }}
                                >
                                    <MenuItem key={'next'} value={'next'}>
                                        Next
                                    </MenuItem>
                                    <MenuItem key={'back'} value={'back'}>
                                        Back
                                    </MenuItem>
                                    {nodes?.length > 0 &&
                                        nodes.map((nd) => (
                                            <MenuItem key={nd?.id} value={nd?.id}>
                                                {nd?.title} - #{nd?.id}
                                            </MenuItem>
                                        ))}
                                    {values?.option_buttons?.[idx]?.button_text && (
                                        <MenuItem key={'new-node'} value={'new-node'} style={{ fontWeight: 'bold' }}>
                                            + New node -&nbsp;
                                            {values?.option_buttons?.[idx]?.button_text}
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                            <FormControl fullWidth>
                                <FormTextField
                                    sx={{ m: 0, minWidth: '100px' }}
                                    placeholder="Answer Value"
                                    name={`option_buttons[${idx}].answer_value`}
                                    size="small"
                                    onChange={(e) => {
                                        const currOptions = [...values.option_buttons];
                                        currOptions[idx] = {
                                            ...values.option_buttons[idx],
                                            answer_value: e.target.value,
                                        };
                                        setFieldValue(`option_buttons`, currOptions);
                                    }}
                                    {...otherProps}
                                />
                            </FormControl>
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                            <FormControl fullWidth>
                                <Select
                                    labelId="btn-style"
                                    className={commonFormStyles.materialSelect}
                                    style={{ margin: 0 }}
                                    id="btn_style"
                                    name={`option_buttons[${idx}].btn_style`}
                                    size="small"
                                    value={button?.style ? button.style : 'neutral'}
                                    onChange={(e) => {
                                        const currOptions = [...values.option_buttons];
                                        currOptions[idx] = {
                                            ...values.option_buttons[idx],
                                            style: e.target.value,
                                        };
                                        setFieldValue('option_buttons', currOptions);
                                    }}
                                >
                                    <MenuItem key={'neutral'} value={'neutral'}>
                                        Neutral
                                    </MenuItem>
                                    <MenuItem key={'positive'} value={'positive'}>
                                        Positive
                                    </MenuItem>
                                    <MenuItem key={'negative'} value={'negative'}>
                                        Negative
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </TableCell>
                        <TableCell padding="none" align="center">
                            <Tooltip title="Is the answer incorrect?">
                                <Checkbox
                                    color="primary"
                                    checked={Boolean(button?.incorrect)}
                                    onChange={(e) => {
                                        const currOptions = [...values.option_buttons];
                                        currOptions[idx] = {
                                            ...values.option_buttons[idx],
                                            incorrect: e.target.checked,
                                        };
                                        setFieldValue('option_buttons', currOptions);
                                    }}
                                />
                            </Tooltip>
                        </TableCell>
                        <TableCell padding="none" align="center">
                            <Tooltip title="Remove button">
                                <IconButton
                                    onClick={() => {
                                        handleRemoveOptionButton(idx);
                                    }}
                                >
                                    <MdDelete />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    </TableRow>
                )}
            </Draggable>
            {openNodeTypeSelectionDialog && (
                <NodeTypeButton
                    openDialog={Boolean(openNodeTypeSelectionDialog)}
                    setOpenDialog={setOpenNodeTypeSelectionDialog}
                    handleButtonSelection={handleButtonSelection}
                />
            )}
        </>
    );
};

export { ButtonGroupField };
